import { Home, Refresh } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import Unauthorized from "layouts/Unauthorized";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CameraModel } from "types";
import { canManageSite, getImagePath } from "utils";
import GetFrame from "./get";
import DrawingStage from "./stage";

export default function Cameras() {
  const [{ userData }] = useContextController();
  const [reload, setReload] = useState(false);
  const {
    isLoading,
    response,
    error,
    callFetch: callFetchGet
  } = useFetch<{
    camera?: CameraModel;
  }>("GET");

  const camera = useMemo(() => response?.data?.camera, [response]);
  const siteId = camera?.box?.siteId || camera?.box?.site?._id;
  const allowed = !camera || canManageSite(userData, siteId);

  const t = useTranslation();
  const frame = useMemo(() => (camera?.frame ? getImagePath(camera.frame) : null), [camera]);

  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const { cameraId } = useParams();

  useEffect(() => {
    if (cameraId) {
      callFetchGet(ENDPOINTS.cameras + "/" + cameraId).catch(console.error);
    }
  }, [reload, callFetchGet, cameraId]);

  return (
    <PrivateLayout canAccess={() => true}>
      {!allowed ? (
        <Unauthorized />
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Breadcrumbs aria-label="breadcrumb">
              <Tooltip title={t["Home"]}>
                <IconButton LinkComponent={Link} {...{ to: "/" }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button LinkComponent={Link} {...{ to: "/cameras" }}>
                {t["Cameras"]}
              </Button>
              {isLoading ? (
                <CircularProgress size="11px" />
              ) : camera ? (
                <Button LinkComponent={Link} {...{ to: `/cameras/${cameraId}` }} size="small">
                  {cameraId}
                </Button>
              ) : (
                <Typography color="error.main">
                  {error ? error.message || JSON.stringify(error) : t["Invalid Camera ID"]}
                </Typography>
              )}
              <Typography color="text.disabled">{t["Region of interests"]}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1}>
              <GetFrame cameraId={camera?._id} onReload={onReload} />
              <Tooltip title={t["Refresh"]}>
                <IconButton onClick={onReload}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {isLoading ? (
                <Skeleton width="100%" height={500} />
              ) : camera ? (
                frame ? (
                  <DrawingStage imgUrl={frame} camera={camera} onReload={onReload} />
                ) : (
                  <Typography color="text.error">{t["Camera has no frame to draw on."]}</Typography>
                )
              ) : (
                <Typography color="text.error">{t["Failed to get camera"]}</Typography>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </PrivateLayout>
  );
}
